<script setup lang="ts">
/**
 * AccessGate — wat een bezoeker ziet vóór afgeschermde documentatie.
 *
 * Drie verschijningsvormen, één formulier:
 * - `variant="page"`  : sitebreed slot, vult het hele scherm.
 * - `variant="inline"`: één afgeschermde categorie binnen een verder open
 *                       site; staat als kaart in het contentgebied zodat de
 *                       navigatie eromheen gewoon zichtbaar blijft.
 * - `variant="dialog"`: dezelfde kaart als overlay, voor het klikken op een
 *                       categorie met een slotje in het menu — die heeft
 *                       geen pagina om naartoe te navigeren.
 *
 * Het scherm staat in de huisstijl van de klant (logo + kleuren komen uit
 * het publieke thema-endpoint), zodat een lezer meteen ziet bij wie hij is.
 */

import { ref, computed, onMounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { Lock, Loader2, LogIn, X } from 'lucide-vue-next'
import { RouterLink, useRoute } from 'vue-router'
import { useAccessStore } from '../../stores/access'
import { useThemeStore } from '../../stores/theme'
import PentricalLogo from '../common/PentricalLogo.vue'

const props = withDefaults(defineProps<{
  variant?: 'page' | 'inline' | 'dialog'
  /** Sitebreed slot: laat leeg. Categorie-slot: het id van die categorie. */
  sectionId?: number | null
  sectionName?: string | null
  mode?: 'password' | 'team'
  hint?: string | null
}>(), {
  variant: 'page',
  sectionId: null,
  sectionName: null,
  mode: 'password',
  hint: null,
})

const emit = defineEmits<{ unlocked: []; close: [] }>()

const access = useAccessStore()
const theme = useThemeStore()
const route = useRoute()
const { t } = useI18n()

/**
 * Inloggen brengt je terug naar de pagina waar je stond, niet naar de
 * homepage — je klikte deze link tenslotte vanaf een specifieke pagina.
 * De /login-route leest deze query (zie LoginView).
 */
const loginLink = computed(() => ({
  path: '/login',
  query: { redirect: route.fullPath },
}))

const password = ref('')
const remember = ref(true)
const isSubmitting = ref(false)
const errorCode = ref<string | null>(null)
const input = ref<HTMLInputElement | null>(null)

const isTeamOnly = computed(() => props.mode === 'team')

const title = computed(() => {
  if (isTeamOnly.value) return t('access.gate.teamTitle')
  return props.sectionName
    ? t('access.gate.categoryTitle', { name: props.sectionName })
    : t('access.gate.title')
})

const errorMessage = computed(() => {
  switch (errorCode.value) {
    case null: return null
    case 'invalid_password': return t('access.gate.wrongPassword')
    case 'tooManyAttempts': return t('access.gate.tooManyAttempts')
    case 'requires_login': return t('access.gate.teamBody')
    case 'not_locked': return t('access.gate.noLongerLocked')
    default: return t('access.gate.failed')
  }
})

onMounted(async () => {
  await nextTick()
  input.value?.focus()
})

async function submit() {
  if (!password.value || isSubmitting.value) return

  isSubmitting.value = true
  errorCode.value = await access.unlock({
    password: password.value,
    remember: remember.value,
    sectionId: props.sectionId,
  })
  isSubmitting.value = false

  if (errorCode.value === null) {
    password.value = ''
    emit('unlocked')
    return
  }

  // Bij een fout het veld leegmaken en de focus teruggeven: anders moet de
  // bezoeker eerst zelf selecteren en wissen.
  if (errorCode.value === 'invalid_password') {
    password.value = ''
    await nextTick()
    input.value?.focus()
  }
}
</script>

<template>
  <div
    :class="{
      'min-h-screen flex items-center justify-center px-4 py-12 bg-gray-50': variant === 'page',
      'flex items-start justify-center px-4 py-10': variant === 'inline',
      'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4': variant === 'dialog',
    }"
    @click.self="variant === 'dialog' && emit('close')"
  >
    <div
      class="w-full max-w-md"
      :role="variant === 'dialog' ? 'dialog' : undefined"
      :aria-modal="variant === 'dialog' ? 'true' : undefined"
      @keydown.esc="variant === 'dialog' && emit('close')"
    >
      <!-- Merk van de klant: logo zoals in de header, zodat het slotscherm
           niet als een vreemd tussenscherm voelt. -->
      <div v-if="variant === 'page'" class="flex justify-center mb-8">
        <img
          v-if="theme.logoType === 'image' && theme.logoUrl"
          :src="theme.logoUrl"
          :alt="theme.logoText"
          class="max-h-12 w-auto"
        />
        <PentricalLogo v-else-if="theme.isDefault" img-class="max-h-10 w-auto" />
        <span v-else class="text-xl font-semibold text-gray-900">{{ theme.logoText }}</span>
      </div>

      <div
        class="relative bg-white border border-gray-200 rounded-xl p-6 sm:p-8"
        :class="variant === 'dialog' ? 'shadow-xl' : 'shadow-sm'"
      >
        <button
          v-if="variant === 'dialog'"
          type="button"
          class="absolute right-3 top-3 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
          :aria-label="t('modal.close')"
          @click="emit('close')"
        >
          <X class="h-5 w-5" aria-hidden="true" />
        </button>

        <div class="flex items-center gap-3">
          <span class="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-primary-50">
            <Lock class="h-5 w-5 text-primary-700" aria-hidden="true" />
          </span>
          <h1 :class="variant === 'page' ? 'text-lg font-bold text-gray-900' : 'text-base font-semibold text-gray-900'">
            {{ title }}
          </h1>
        </div>

        <!-- Alleen-team: geen wachtwoord, wel een weg naar binnen. -->
        <template v-if="isTeamOnly">
          <p class="mt-4 text-sm text-gray-600">{{ t('access.gate.teamBody') }}</p>
          <RouterLink :to="loginLink" class="btn-primary mt-6 inline-flex items-center gap-2">
            <LogIn class="h-4 w-4" aria-hidden="true" />
            {{ t('common.login') }}
          </RouterLink>
        </template>

        <template v-else>
          <p class="mt-4 text-sm text-gray-600">
            {{ sectionName ? t('access.gate.categoryBody') : t('access.gate.body') }}
          </p>
          <p v-if="hint" class="mt-2 text-sm text-gray-500">{{ hint }}</p>

          <form class="mt-6" novalidate @submit.prevent="submit">
            <label :for="`access-password-${sectionId ?? 'site'}`" class="block text-sm font-medium text-gray-700">
              {{ t('access.gate.passwordLabel') }}
            </label>
            <input
              :id="`access-password-${sectionId ?? 'site'}`"
              ref="input"
              v-model="password"
              type="password"
              autocomplete="current-password"
              class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              :aria-invalid="errorCode ? 'true' : undefined"
              :aria-describedby="errorCode ? `access-error-${sectionId ?? 'site'}` : undefined"
            />

            <p
              v-if="errorMessage"
              :id="`access-error-${sectionId ?? 'site'}`"
              class="mt-2 text-sm text-red-700"
              role="alert"
            >
              {{ errorMessage }}
            </p>

            <label class="mt-4 flex items-center gap-2 text-sm text-gray-700">
              <input
                v-model="remember"
                type="checkbox"
                class="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              {{ t('access.gate.remember') }}
            </label>

            <button
              type="submit"
              class="btn-primary mt-6 w-full justify-center"
              :disabled="!password || isSubmitting"
            >
              <Loader2 v-if="isSubmitting" class="h-4 w-4 animate-spin" aria-hidden="true" />
              {{ t('access.gate.submit') }}
            </button>
          </form>

          <p class="mt-6 text-center text-xs text-gray-400">
            {{ t('access.gate.memberFooter') }}
            <RouterLink :to="loginLink" class="text-primary-700 hover:underline">{{ t('common.login') }}</RouterLink>
          </p>
        </template>
      </div>
    </div>
  </div>
</template>
